import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DateSelector from './DateSelector';
import { calculateDaysDifference, calculateDetailedDifference, calculateWorkdaysDifference } from '../utils/dateUtils';
import SafeIcon from '../common/SafeIcon';
import { FiInfo, FiHelpCircle } from 'react-icons/fi';

const DateCalculator = ({ language, onDataUpdate }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [calculationMode, setCalculationMode] = useState('all');
  const [results, setResults] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [showWorkdayInfo, setShowWorkdayInfo] = useState(false);

  // Extract URL parameters for SEO-friendly deep linking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const startParam = params.get('start');
    const endParam = params.get('end');
    const modeParam = params.get('mode');
    
    if (startParam) {
      const [year, month, day] = startParam.split('-').map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        setStartDate(new Date(year, month - 1, day));
      }
    }
    
    if (endParam) {
      const [year, month, day] = endParam.split('-').map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        setEndDate(new Date(year, month - 1, day));
      }
    }
    
    if (modeParam && ['all', 'workdays'].includes(modeParam)) {
      setCalculationMode(modeParam);
    }
  }, []);

  // Automatically calculate when both dates are set
  useEffect(() => {
    if (startDate && endDate) {
      handleCalculate();
    }
  }, [startDate, endDate, calculationMode]);

  const handleCalculate = () => {
    if (!startDate || !endDate) {
      return;
    }

    // Show animation effect
    setShowAnimation(true);
    setTimeout(() => setShowAnimation(false), 1000);

    let daysDifference;
    if (calculationMode === 'all') {
      daysDifference = Math.abs(calculateDaysDifference(startDate, endDate));
    } else {
      daysDifference = Math.abs(calculateWorkdaysDifference(startDate, endDate));
    }

    const earlierDate = startDate < endDate ? startDate : endDate;
    const laterDate = startDate < endDate ? endDate : startDate;
    const detailedTime = calculateDetailedDifference(earlierDate, laterDate);

    const newResults = {
      days: daysDifference,
      detailed: detailedTime,
      startDate: earlierDate,
      endDate: laterDate
    };
    
    setResults(newResults);
    
    // Update URL with parameters for shareable links without page reload
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('start', `${earlierDate.getFullYear()}-${earlierDate.getMonth() + 1}-${earlierDate.getDate()}`);
    newUrl.searchParams.set('end', `${laterDate.getFullYear()}-${laterDate.getMonth() + 1}-${laterDate.getDate()}`);
    newUrl.searchParams.set('mode', calculationMode);
    window.history.replaceState({}, '', newUrl);
    
    // Pass data to parent for SEO optimization
    if (onDataUpdate) {
      onDataUpdate({
        startDate: earlierDate,
        endDate: laterDate,
        calculationMode,
        days: daysDifference,
        detailed: detailedTime
      });
    }
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setResults(null);
    
    // Remove date parameters from URL
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('start');
    newUrl.searchParams.delete('end');
    window.history.replaceState({}, '', newUrl);
  };

  // Function to format detailed time units with proper labels
  const formatTimeUnit = (value, unit) => {
    if (language === 'zh') {
      const units = {
        years: '年',
        months: '個月',
        days: '天'
      };
      return `${value} ${units[unit]}`;
    } else {
      if (value === 1) {
        return `${value} ${unit.slice(0, -1)}`;
      }
      return `${value} ${unit}`;
    }
  };

  return (
    <div className="p-6 bg-gradient-to-br from-white to-love-50 dark:from-gray-800 dark:to-gray-850">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Date Selection */}
        <motion.div 
          className="space-y-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <DateSelector
            language={language}
            selectedDate={startDate}
            onDateChange={setStartDate}
            label={language === 'zh' ? '開始日期' : 'Start Date'}
            icon="📅"
            allowFuture={false}
          />

          <DateSelector
            language={language}
            selectedDate={endDate}
            onDateChange={setEndDate}
            label={language === 'zh' ? '結束日期' : 'End Date'}
            icon="📅"
            allowFuture={true}
          />

          {/* Enhanced Calculation Mode Selector with Info Icon */}
          <motion.div 
            className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <span className="emoji mr-1">⚙️</span>
                {language === 'zh' ? '計算模式' : 'Calculation Mode'}
              </label>
              
              {/* Info button for workday calculation explanation */}
              {calculationMode === 'workdays' && (
                <button
                  onClick={() => setShowWorkdayInfo(!showWorkdayInfo)}
                  className="text-gray-500 hover:text-love-500 dark:text-gray-400 dark:hover:text-love-400 transition-colors focus:outline-none"
                  aria-label={language === 'zh' ? '工作日說明' : 'Workday Calculation Info'}
                >
                  <SafeIcon icon={FiHelpCircle} className="text-lg" />
                </button>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  calculationMode === 'all' 
                    ? 'bg-love-500 text-white shadow-md' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => {
                  setCalculationMode('all');
                  // Update URL when mode changes
                  const newUrl = new URL(window.location);
                  newUrl.searchParams.set('mode', 'all');
                  window.history.replaceState({}, '', newUrl);
                }}
                aria-label={language === 'zh' ? '所有日期' : 'All Days'}
              >
                {language === 'zh' ? '所有日期' : 'All Days'}
              </button>
              <button
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  calculationMode === 'workdays' 
                    ? 'bg-love-500 text-white shadow-md' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => {
                  setCalculationMode('workdays');
                  // Update URL when mode changes
                  const newUrl = new URL(window.location);
                  newUrl.searchParams.set('mode', 'workdays');
                  window.history.replaceState({}, '', newUrl);
                }}
                aria-label={language === 'zh' ? '工作日' : 'Workdays Only'}
              >
                {language === 'zh' ? '工作日' : 'Workdays Only'}
              </button>
            </div>
            
            {/* Workday calculation explanation panel */}
            <AnimatePresence>
              {showWorkdayInfo && (
                <motion.div
                  className="mt-3 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-gray-100 dark:border-gray-700"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-start">
                    <SafeIcon icon={FiInfo} className="mr-2 text-love-500 mt-0.5 flex-shrink-0" />
                    <div>
                      {language === 'zh' ? (
                        <>
                          <p className="font-medium mb-1">工作日計算標準:</p>
                          <ul className="list-disc pl-4 space-y-1">
                            <li>週一至週五被視為工作日</li>
                            <li>週六和週日被視為週末（非工作日）</li>
                            <li>不包含法定假期</li>
                            <li>計算結果僅供參考，實際工作日可能因國家/地區和公司政策而異</li>
                          </ul>
                        </>
                      ) : (
                        <>
                          <p className="font-medium mb-1">Workday Calculation Standard:</p>
                          <ul className="list-disc pl-4 space-y-1">
                            <li>Monday to Friday are counted as workdays</li>
                            <li>Saturdays and Sundays are counted as weekends (non-workdays)</li>
                            <li>Public holidays are not excluded</li>
                            <li>Results are for reference only and may vary by country/region and company policies</li>
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <div className="flex space-x-3">
            <button
              onClick={handleCalculate}
              disabled={!startDate || !endDate}
              className="flex-1 bg-gradient-to-r from-love-500 to-love-600 hover:from-love-600 hover:to-love-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-3 px-4 rounded-lg transition shadow-sm hover:shadow-md"
              aria-label={language === 'zh' ? '重新計算' : 'Recalculate'}
            >
              {language === 'zh' ? '重新計算' : 'Recalculate'}
            </button>
            <button
              onClick={handleReset}
              className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-3 px-4 rounded-lg transition-all duration-300"
              aria-label={language === 'zh' ? '重置' : 'Reset'}
            >
              {language === 'zh' ? '重置' : 'Reset'}
            </button>
          </div>
        </motion.div>

        {/* Enhanced Results Display */}
        <motion.div
          className="bg-gradient-to-br from-white to-love-50 dark:from-gray-800 dark:to-gray-850 p-5 rounded-xl border border-love-100 dark:border-love-900 shadow-sm"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <span className="emoji mr-2">📊</span>
            {language === 'zh' ? '計算結果' : 'Results'}
          </h3>

          <AnimatePresence>
            {results ? (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                {/* Animated Total Days Counter */}
                <motion.div 
                  className="text-center p-4 mb-4 bg-white dark:bg-gray-750 rounded-lg shadow-sm border border-love-100 dark:border-love-900"
                  animate={showAnimation ? { scale: [1, 1.05, 1] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-5xl font-bold text-love-500 mb-2">
                    {results.days}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {language === 'zh' ? '總天數' : 'Total Days'}
                    {calculationMode === 'workdays' && 
                      ` (${language === 'zh' ? '工作日' : 'Workdays'})`
                    }
                  </div>
                </motion.div>

                {/* Enhanced Detailed Breakdown */}
                <div className="bg-white dark:bg-gray-750 p-4 rounded-lg mb-5 shadow-sm border border-love-100 dark:border-love-900">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {language === 'zh' ? '詳細時間' : 'Detailed Time'}
                  </h4>
                  
                  <div className="flex items-center justify-center">
                    {results.detailed.years > 0 && (
                      <motion.div 
                        className="flex-1 text-center"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        <div className="text-2xl font-semibold text-love-700 dark:text-love-300">
                          {results.detailed.years}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {language === 'zh' ? '年' : 
                           results.detailed.years === 1 ? 'Year' : 'Years'}
                        </div>
                      </motion.div>
                    )}
                    
                    {(results.detailed.years > 0 || results.detailed.months > 0) && (
                      <motion.div 
                        className="flex-1 text-center"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <div className="text-2xl font-semibold text-love-700 dark:text-love-300">
                          {results.detailed.months}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {language === 'zh' ? '月' : 
                           results.detailed.months === 1 ? 'Month' : 'Months'}
                        </div>
                      </motion.div>
                    )}
                    
                    <motion.div 
                      className="flex-1 text-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="text-2xl font-semibold text-love-700 dark:text-love-300">
                        {results.detailed.days}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {language === 'zh' ? '日' : 
                         results.detailed.days === 1 ? 'Day' : 'Days'}
                      </div>
                    </motion.div>
                  </div>
                  
                  {/* Human-readable summary */}
                  <motion.div 
                    className="mt-3 text-center text-sm text-gray-600 dark:text-gray-400"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {language === 'zh' ? 
                      `共計 ${results.detailed.years ? `${results.detailed.years}年` : ''} ${results.detailed.months ? `${results.detailed.months}個月` : ''} ${results.detailed.days}天` :
                      `${results.detailed.years ? formatTimeUnit(results.detailed.years, 'years') + ' ' : ''}${results.detailed.months ? formatTimeUnit(results.detailed.months, 'months') + ' ' : ''}${formatTimeUnit(results.detailed.days, 'days')}`
                    }
                  </motion.div>
                </div>

                {/* Enhanced Timeline */}
                <div className="mt-8">
                  <div className="timeline rounded-full mt-8 mb-10">
                    <div className="timeline-dot" style={{ left: '0%' }}>
                      <div className="timeline-label">
                        {language === 'zh' ? 
                          `${results.startDate.getFullYear()}/${results.startDate.getMonth() + 1}/${results.startDate.getDate()}` : 
                          results.startDate.toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' })
                        }
                      </div>
                    </div>
                    <div className="timeline-dot" style={{ left: '100%' }}>
                      <div className="timeline-label">
                        {language === 'zh' ? 
                          `${results.endDate.getFullYear()}/${results.endDate.getMonth() + 1}/${results.endDate.getDate()}` : 
                          results.endDate.toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' })
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="w-24 h-24 mb-4 opacity-50 animate-float">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <path d="M8 14h2v2H8zM12 14h2v2h-2zM16 14h2v2h-2zM8 18h2v2H8z"></path>
                  </svg>
                </div>
                <p>{language === 'zh' ? '請選擇開始和結束日期' : 'Please select start and end dates'}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default DateCalculator;